import { ApiExplorerObjectInput } from '@squareup/dex-types-shared-api-explorer';
import { filterAndSortApiExplorerProperties } from '@squareup/dex-utils-shared-api-explorer';
import { isObject } from '@squareup/dex-utils-shared-json';
import React, { FC, memo, useCallback, useMemo } from 'react';

import { ApiExplorerField } from './ApiExplorerField';
import { ApiExplorerFieldAddButton } from './helpers/ApiExplorerFieldAddButton';
import { ApiExplorerFieldAddButtonSpecialValuesDropdown } from './helpers/ApiExplorerFieldAddButtonSpecialValuesDropdown';
import { ApiExplorerFieldEntityProps } from './props';
import { useApiExplorerFieldContext } from './providers/ApiExplorerFieldProvider';

interface Props extends ApiExplorerFieldEntityProps {}

const strings = {
  addButton: 'Add',
};

const ApiExplorerObjectField: FC<Props> = memo(
  ({ currentValue, depth = 0, entity, onChange, path }) => {
    const { isNullable } = useApiExplorerFieldContext();

    const onObjectAdded = useCallback(() => {
      onChange({}, path);
    }, [onChange, path]);

    const noObjectState = (
      <ApiExplorerFieldAddButton
        onClick={onObjectAdded}
        trackingId={`api-explorer-object-add-button-${entity.id}`}
        extraValuesTrigger={
          isNullable ? (
            <ApiExplorerFieldAddButtonSpecialValuesDropdown
              trackingId={`api-explorer-object-add-button-extra-values-${entity.id}`}
              path={path}
              onChange={onChange}
              currentValue={currentValue}
            />
          ) : null
        }
      >
        {strings.addButton}
      </ApiExplorerFieldAddButton>
    );

    const objectEntities = useMemo(() => {
      // If we have a type field, let's make it easier on the user by filtering
      // out any other _data fields they don't care about
      // This is supported in endpoints like UpsertCatalog, CreateSubscription, etc
      const maybeTypeFieldValue = (currentValue as ApiExplorerObjectInput)?.[
        'type'
      ];

      const typeFieldValue =
        typeof maybeTypeFieldValue === 'string'
          ? maybeTypeFieldValue
          : undefined;

      return filterAndSortApiExplorerProperties(entity.properties, {
        typeFieldValue,
      }).map((entityProperty) => {
        return (
          <ApiExplorerField
            currentValue={currentValue as ApiExplorerObjectInput}
            depth={depth + 1}
            key={entityProperty.id}
            property={entityProperty}
            path={path}
            onChange={onChange}
          />
        );
      });
    }, [currentValue, depth, entity.properties, onChange, path]);

    return !isObject(currentValue) ? noObjectState : objectEntities;
  }
);

ApiExplorerObjectField.displayName = 'ApiExplorerObjectField';

export { ApiExplorerObjectField, type Props as ApiExplorerObjectFieldProps };
